/**
 * Which column is the date, which is the description, which is the money.
 *
 * Every bank exports a different CSV. Some write a header row and some do not.
 * Some have one `Amount` column, some split `Debit` and `Credit`. Some write
 * a debit as a negative number and some write it as a positive one. None of
 * that is knowable in advance, so this SNIFFS: the header when there is one,
 * the rows themselves when there is not.
 *
 * The one rule that shapes the whole file: it returns what it decided and how
 * sure it is, never a silent guess. A wrong date column moves a month of
 * spending; a wrong sign turns a refund into an expense. The UI shows this
 * back to the owner and lets him correct it before a single row is reviewed.
 *
 * Pure and Node-free — the browser does this (spec ruling 2).
 */

import { detectDateOrder, parseDateCell, type DateOrder } from './dates';

export type AmountShape = 'single' | 'debit_credit';
export type Confidence = 'high' | 'medium' | 'low';

export interface ColumnMapping {
  hasHeader: boolean;
  /** The header row as written, or [] when the file has none. */
  headers: string[];
  dateIndex: number;
  descriptionIndex: number;
  /** The one money column. -1 when the file splits debit and credit. */
  amountIndex: number;
  /** -1 unless `shape` is 'debit_credit'. */
  debitIndex: number;
  creditIndex: number;
  shape: AmountShape;
  dateOrder: DateOrder;
  /**
   * In a single money column, does a POSITIVE number mean money OUT?
   *
   * Both conventions are in the wild: a chequing export writes a debit as
   * -42.10, a card export writes the purchase as 42.10 and the payment as
   * -500.00. Ignored when the file splits debit and credit, which says which
   * is which by the column it put the figure in.
   */
  debitIsPositive: boolean;
}

export interface ColumnGuess {
  mapping: ColumnMapping;
  confidence: Confidence;
  /** What was decided and on what evidence. Shown to the owner. */
  notes: string[];
  /** Why it might be wrong. A non-empty list is what makes the UI gate. */
  warnings: string[];
  /** The rows below the header — what `buildLines` reads. */
  dataRows: string[][];
}

/* ==========================================================================
 * Cells
 * ======================================================================== */

/**
 * A money cell as a number, or null when it is not money.
 *
 * Handles what statements actually print: a currency symbol, thousands
 * separators, a trailing minus, and accountants' parentheses for a negative.
 */
export function parseAmount(text: string): number | null {
  let cell = (text ?? '').trim();
  if (cell === '') return null;

  let negative = false;
  if (/^\(.*\)$/.test(cell)) {
    negative = true;
    cell = cell.slice(1, -1).trim();
  }
  // A minus printed after the figure, as some exports do.
  if (/-$/.test(cell)) {
    negative = true;
    cell = cell.slice(0, -1).trim();
  }
  cell = cell.replace(/[$€£¥]/g, '').replace(/\s/g, '').replace(/,/g, '');
  if (!/^[+-]?\d+(\.\d+)?$/.test(cell)) return null;

  const value = Number(cell);
  if (!Number.isFinite(value)) return null;
  return negative ? -value : value;
}

/** True when a cell reads as a date under any of the three orders. */
function looksLikeDate(cell: string): boolean {
  return parseDateCell(cell, 'ymd') !== null || parseDateCell(cell, 'mdy') !== null;
}

/* ==========================================================================
 * Header sniffing
 * ======================================================================== */

const DATE_WORDS = /\b(date|posted|posting)\b/i;
const DESC_WORDS = /(description|details|narrative|merchant|payee|memo|particular|reference|name)/i;
const AMOUNT_WORDS = /(amount|value|total|charge)/i;
const DEBIT_WORDS = /(debit|withdrawal|withdrawn|money\s*out|paid\s*out|payment\s*out|spent)/i;
const CREDIT_WORDS = /(credit|deposit|money\s*in|paid\s*in|refund|received)/i;
const BALANCE_WORDS = /(balance)/i;

/**
 * Is row 0 a header?
 *
 * The strong signal is negative: a header row holds no date and no money,
 * because the words that name those columns are not one. Keyword hits confirm
 * it, and are what saves a file whose first data row happens to be blank.
 */
function looksLikeHeader(row: readonly string[]): boolean {
  const cells = row.map((c) => (c ?? '').trim());
  const hasData = cells.some((c) => looksLikeDate(c)) && cells.some((c) => parseAmount(c) !== null);
  if (hasData) return false;
  const named = cells.filter(
    (c) => DATE_WORDS.test(c) || DESC_WORDS.test(c) || AMOUNT_WORDS.test(c) || DEBIT_WORDS.test(c) || CREDIT_WORDS.test(c)
  ).length;
  return named >= 2 || cells.every((c) => c !== '' && parseAmount(c) === null && !looksLikeDate(c));
}

function findHeader(headers: readonly string[], pattern: RegExp, taken: readonly number[]): number {
  for (let i = 0; i < headers.length; i++) {
    if (taken.includes(i)) continue;
    if (pattern.test((headers[i] ?? '').trim())) return i;
  }
  return -1;
}

/* ==========================================================================
 * Column profiles, for a file with no header
 * ======================================================================== */

interface ColumnProfile {
  index: number;
  nonEmpty: number;
  dates: number;
  numbers: number;
  /** Cells that are neither a date nor a number — a description looks like this. */
  words: number;
  /** Longest cell seen, which separates a description from a code. */
  longest: number;
}

const SAMPLE_ROWS = 60;

function profile(rows: readonly string[][]): ColumnProfile[] {
  const width = rows.reduce((w, row) => Math.max(w, row.length), 0);
  const sample = rows.slice(0, SAMPLE_ROWS);
  const profiles: ColumnProfile[] = [];
  for (let i = 0; i < width; i++) {
    const p: ColumnProfile = { index: i, nonEmpty: 0, dates: 0, numbers: 0, words: 0, longest: 0 };
    for (const row of sample) {
      const cell = (row[i] ?? '').trim();
      if (cell === '') continue;
      p.nonEmpty++;
      p.longest = Math.max(p.longest, cell.length);
      if (looksLikeDate(cell)) p.dates++;
      else if (parseAmount(cell) !== null) p.numbers++;
      else p.words++;
    }
    profiles.push(p);
  }
  return profiles;
}

function bestBy(
  profiles: readonly ColumnProfile[],
  taken: readonly number[],
  score: (p: ColumnProfile) => number
): number {
  let best = -1;
  let bestScore = 0;
  for (const p of profiles) {
    if (taken.includes(p.index)) continue;
    const s = score(p);
    if (s > bestScore) {
      bestScore = s;
      best = p.index;
    }
  }
  return best;
}

/* ==========================================================================
 * The guess
 * ======================================================================== */

function emptyMapping(): ColumnMapping {
  return {
    hasHeader: false,
    headers: [],
    dateIndex: -1,
    descriptionIndex: -1,
    amountIndex: -1,
    debitIndex: -1,
    creditIndex: -1,
    shape: 'single',
    dateOrder: 'ymd',
    debitIsPositive: true
  };
}

/**
 * Work out the mapping for a parsed CSV, with the confidence to match.
 *
 * `rows` is everything `parseCsvRows` returned, header included.
 */
export function detectColumns(rows: string[][]): ColumnGuess {
  const notes: string[] = [];
  const warnings: string[] = [];

  if (rows.length === 0) {
    return {
      mapping: emptyMapping(),
      confidence: 'low',
      notes,
      warnings: ['The file has no rows.'],
      dataRows: []
    };
  }

  const hasHeader = looksLikeHeader(rows[0]);
  const headers = hasHeader ? rows[0].map((c) => (c ?? '').trim()) : [];
  const dataRows = hasHeader ? rows.slice(1) : rows;
  notes.push(hasHeader ? 'The first row is column names.' : 'The file has no header row.');

  const profiles = profile(dataRows);
  const taken: number[] = [];
  let byName = 0;

  /* --- date ------------------------------------------------------------- */
  let dateIndex = hasHeader ? findHeader(headers, DATE_WORDS, taken) : -1;
  if (dateIndex >= 0) {
    byName++;
    notes.push(`Date: “${headers[dateIndex]}”.`);
  } else {
    // Half the cells parsing as a date is a low bar deliberately: a column of
    // dates with a few blanks is still the date column.
    dateIndex = bestBy(profiles, taken, (p) => (p.nonEmpty > 0 && p.dates / p.nonEmpty >= 0.5 ? p.dates : 0));
    if (dateIndex >= 0) notes.push(`Date: column ${dateIndex + 1}, which holds dates.`);
  }
  if (dateIndex < 0) warnings.push('No column looks like a date. Choose one.');
  else taken.push(dateIndex);

  /* --- money ------------------------------------------------------------ */
  let debitIndex = -1;
  let creditIndex = -1;
  let amountIndex = -1;
  let shape: AmountShape = 'single';

  if (hasHeader) {
    debitIndex = findHeader(headers, DEBIT_WORDS, taken);
    creditIndex = findHeader(headers, CREDIT_WORDS, taken);
  }
  if (debitIndex >= 0 && creditIndex >= 0) {
    shape = 'debit_credit';
    byName++;
    taken.push(debitIndex, creditIndex);
    notes.push(`Money out: “${headers[debitIndex]}”. Money in: “${headers[creditIndex]}”.`);
  } else {
    debitIndex = -1;
    creditIndex = -1;
    if (hasHeader) {
      // A balance column is money too, and naming it Amount would put the
      // running balance in the P&L, so it is excluded by name first.
      const balance = findHeader(headers, BALANCE_WORDS, taken);
      if (balance >= 0) {
        taken.push(balance);
        notes.push(`Ignoring “${headers[balance]}” — a running balance is not a transaction.`);
      }
      amountIndex = findHeader(headers, AMOUNT_WORDS, taken);
      if (amountIndex >= 0) {
        byName++;
        notes.push(`Amount: “${headers[amountIndex]}”.`);
      }
    }
    if (amountIndex < 0) {
      const numericColumns = profiles.filter(
        (p) => !taken.includes(p.index) && p.nonEmpty > 0 && p.numbers / p.nonEmpty >= 0.8
      );
      if (numericColumns.length >= 2) {
        // Two money columns and no names to tell them apart: the one filled in
        // more often is the spending, because a statement is mostly spending.
        const [first, second] = [...numericColumns].sort((a, b) => b.nonEmpty - a.nonEmpty);
        shape = 'debit_credit';
        debitIndex = first.index;
        creditIndex = second.index;
        taken.push(debitIndex, creditIndex);
        warnings.push(
          `Two money columns and no names: reading column ${debitIndex + 1} as money out and column ${creditIndex + 1} as money in. Swap them if that is backwards.`
        );
      } else if (numericColumns.length === 1) {
        amountIndex = numericColumns[0].index;
        notes.push(`Amount: column ${amountIndex + 1}, the only money column.`);
      }
    }
    if (amountIndex >= 0) taken.push(amountIndex);
  }
  if (shape === 'single' && amountIndex < 0) {
    warnings.push('No column looks like money. Choose one.');
  }

  /* --- description ------------------------------------------------------ */
  let descriptionIndex = hasHeader ? findHeader(headers, DESC_WORDS, taken) : -1;
  if (descriptionIndex >= 0) {
    byName++;
    notes.push(`Description: “${headers[descriptionIndex]}”.`);
  } else {
    descriptionIndex = bestBy(profiles, taken, (p) => p.words * 100 + p.longest);
    if (descriptionIndex >= 0) notes.push(`Description: column ${descriptionIndex + 1}.`);
  }
  if (descriptionIndex < 0) {
    warnings.push('No column looks like a description. Choose one, or leave the lines unnamed.');
  } else {
    taken.push(descriptionIndex);
  }

  /* --- how the dates read ----------------------------------------------- */
  const dateCells = dateIndex >= 0 ? dataRows.map((row) => row[dateIndex] ?? '') : [];
  const order = detectDateOrder(dateCells);
  notes.push(order.reason);
  if (order.ambiguous && dateIndex >= 0) warnings.push(order.reason);

  /* --- which way the signs run ------------------------------------------ */
  let debitIsPositive = true;
  if (shape === 'single' && amountIndex >= 0) {
    let positives = 0;
    let negatives = 0;
    for (const row of dataRows) {
      const value = parseAmount(row[amountIndex] ?? '');
      if (value === null || value === 0) continue;
      if (value > 0) positives++;
      else negatives++;
    }
    debitIsPositive = positives >= negatives;
    const outward = debitIsPositive ? positives : negatives;
    const inward = debitIsPositive ? negatives : positives;
    notes.push(
      debitIsPositive
        ? `Signs: a positive amount is money out (${positives} of them, ${negatives} negative).`
        : `Signs: a negative amount is money out (${negatives} of them, ${positives} positive).`
    );
    // A statement is mostly spending. When the two sides are near even, that
    // assumption has nothing behind it and the owner has to look.
    if (outward > 0 && inward > 0 && outward < inward * 2) {
      warnings.push(
        'Money out and money in are close to evenly split, so which sign means which is a guess. Check it.'
      );
    }
    if (positives === 0 && negatives === 0) warnings.push('Every amount is zero or unreadable.');
  }

  const mapping: ColumnMapping = {
    hasHeader,
    headers,
    dateIndex,
    descriptionIndex,
    amountIndex,
    debitIndex,
    creditIndex,
    shape,
    dateOrder: order.order,
    debitIsPositive
  };

  const named = hasHeader && byName >= 3;
  const confidence: Confidence =
    warnings.length > 0 ? 'low' : named ? 'high' : 'medium';

  return { mapping, confidence, notes, warnings, dataRows };
}
